import { AiOutlineInbox } from "react-icons/ai";
import { projectTertiaryColor } from "../../../generalConfigs";
import useIcons from "../../../hooks/useIcons";
import { parseAssetTypeInt } from "../../../utils";
import { useEffect, useRef, useState } from "react";
import { isEmpty } from "lodash";
import LoadingButton from "../../shared/LoadingButton";
import { isMobile } from "react-device-detect";
import all from "../../../services/user/preferences/assets/all";
import store from "../../../services/user/preferences/assets/store";
import destroy from "../../../services/user/preferences/assets/destroy";
import useAssets from "../../../hooks/useAssets";
import useGeneralContext from "../../../context/GeneralContext";
import useFollowingAssets from "../../../hooks/useFollowingAssets";
import { AssetSuggestion } from "../../../@types/Asset";
import { useHistory } from "react-router-dom";

type Props = {
  doRefreshPosts: CallableFunction;
};
export function EmptyState({ doRefreshPosts }: Props) {
  const { userFollowingAssets, setUserFollowingAssets } = useGeneralContext();
  const { addStateAsset, removeStateAsset } = useFollowingAssets();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingEncodedId, setLoadingEncodedId] = useState<string>("");
  const { getURI } = useIcons();
  const { getSuggestions } = useAssets();
  const randomSuggestionsRef = useRef(getSuggestions(5));
  const [suggestions, setSuggestions] = useState<AssetSuggestion[]>([]);
  const { push } = useHistory();

  function isFollowing(encodedId: string): boolean {
    return userFollowingAssets.some((asset) => asset.encoded_id === encodedId);
  }

  function determineSuggestions(): void {
    const newSuggestions = randomSuggestionsRef.current.map((asset) => {
      if (isFollowing(asset.id)) {
        asset.status = true;
      } else {
        asset.status = false;
      }

      return asset;
    });

    setSuggestions(newSuggestions);
  }

  async function handleFollow(asset: AssetSuggestion) {
    setIsLoading(true);
    const encodedId = asset.id;
    setLoadingEncodedId(encodedId);

    const storeResponse = await store(encodedId);

    if (storeResponse) {
      addStateAsset({
        name: asset.name,
        country_code: asset.country_code,
        currency_symbol: asset.currency_symbol,
        encoded_id: asset.id,
        exchange: asset.exchange,
        icon: asset.icon,
        status: 1,
        symbol: asset.symbol,
        type: asset.type,
      });
    }

    setIsLoading(false);
    setLoadingEncodedId("");
    await doRefreshPosts();
  }

  async function handleUnfollow(encodedId: string) {
    setIsLoading(true);
    setLoadingEncodedId(encodedId);

    const destroyResponse = await destroy(encodedId);

    if (destroyResponse) {
      removeStateAsset(encodedId);
    }

    setIsLoading(false);
    setLoadingEncodedId("");
  }

  useEffect(() => {
    async function fetchUserFollowing() {
      const userFollowingResponse = await all();

      if (userFollowingResponse?.status === 1) {
        const userFollowingAssets = userFollowingResponse.data;

        if (userFollowingAssets.length > 0) {
          setUserFollowingAssets(userFollowingAssets);
        }
      }
    }

    fetchUserFollowing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //Update suggestions according to context
  useEffect(() => {
    determineSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFollowingAssets]);

  return (
    <div className="az-content pd-t-20 pd-b-20">
      <div className="az-content-body">
        <div className="row row-sm">
          <div className="col-md">
            <div className="card card-minimal-four">
              <div className="card-header">
                <div style={{ textAlign: "center" }}>
                  <span>
                    <AiOutlineInbox size={48} />
                  </span>
                  <h4>Sem publicações, por enquanto.</h4>
                  <span>
                    Não encontramos publicações disponíveis dos ativos que você
                    segue, que tal seguir novos ativos?
                  </span>

                  <div className="mg-t-20">
                    {!isEmpty(suggestions) &&
                      suggestions.map((asset: any, i: number) => {
                        const buttonBackground = asset.status
                          ? "gray"
                          : projectTertiaryColor;

                        const buttonText = asset.status ? "Seguindo" : "Seguir";

                        return (
                          <li
                            className="list-group-item d-flex align-items-center cursor-pointer"
                            onClick={() => push(`/analysis/${asset.id}`)}
                            style={{ borderRadius: 10, marginTop: 5 }}
                            key={i}
                          >
                            <img
                              src={getURI(asset.icon)}
                              className="wd-30 rounded-circle mg-r-15"
                              alt=""
                            />
                            {isMobile ? (
                              <div>
                                <h6 className="tx-13 tx-semibold mg-b-0">
                                  {asset.name}
                                </h6>
                                <span className="d-block tx-11 text-muted">
                                  {parseAssetTypeInt(asset.type)}
                                  {asset.exchange &&
                                    " (" + asset.exchange + ")"}
                                </span>
                              </div>
                            ) : (
                              <>
                                <h6 className="tx-13 tx-inverse tx-semibold mg-b-0 mg-r-5">
                                  {asset.name}
                                </h6>
                                <span className="d-block tx-11 text-muted mg-b-3">
                                  {parseAssetTypeInt(asset.type)}
                                  {asset.exchange &&
                                    " (" + asset.exchange + ")"}
                                </span>
                              </>
                            )}

                            <div
                              style={{
                                padding: 2,
                                marginLeft: "auto",
                              }}
                            >
                              {isLoading && asset.id === loadingEncodedId ? (
                                <LoadingButton
                                  backgroundColor={buttonBackground}
                                  textColor="white"
                                />
                              ) : (
                                <button
                                  className={`btn btn-rounded btn-sm ${
                                    isLoading ? "disabled" : ""
                                  }`}
                                  type="button"
                                  style={{
                                    backgroundColor: buttonBackground,
                                    color: "white",
                                  }}
                                  onClick={(e) => {
                                    if (isLoading) {
                                      return;
                                    }

                                    e.stopPropagation();

                                    if (asset.status) {
                                      handleUnfollow(asset.id);
                                    } else {
                                      handleFollow(asset);
                                    }
                                  }}
                                >
                                  {buttonText}
                                </button>
                              )}
                            </div>
                          </li>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
